import { z } from 'zod';

export const emailSchema = z.string().trim().toLowerCase().pipe(z.email({ error: 'Adresse e-mail invalide' }));

/** Politique de mot de passe : 10 caractères minimum, au moins une lettre et un chiffre. */
export const passwordSchema = z
  .string()
  .min(10, { error: 'Le mot de passe doit contenir au moins 10 caractères' })
  .max(200)
  .regex(/[A-Za-zÀ-ÿ]/, { error: 'Le mot de passe doit contenir au moins une lettre' })
  .regex(/\d/, { error: 'Le mot de passe doit contenir au moins un chiffre' });

export const signupSchema = z.object({
  companyName: z.string().trim().min(2).max(160),
  countryCode: z.string().length(2).toUpperCase().default('TD'),
  fullName: z.string().trim().min(2).max(120),
  email: emailSchema,
  password: passwordSchema,
});
export type SignupInput = z.infer<typeof signupSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const userStatusSchema = z.enum(['ACTIVE', 'DISABLED']);

/** Mode d'utilisation : masque des écrans et des champs ; aucune donnée n'est modifiée en changeant de mode. */
export const UI_MODES = ['SIMPLE', 'PRO', 'EXPERT'] as const;
export const uiModeSchema = z.enum(UI_MODES);
export type UiMode = z.infer<typeof uiModeSchema>;

export const sessionUserSchema = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  companyName: z.string(),
  email: z.string(),
  fullName: z.string(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  uiMode: uiModeSchema,
  /** Devise par défaut de l'entreprise (écrans hors projet : catalogues, tarifs). */
  companyCurrency: z.string(),
  /** Abonnement suspendu ou résilié : écritures bloquées, consultation et exports ouverts. */
  readOnly: z.boolean(),
  /** Administrateur général de la plateforme (attribué en ligne de commande sur le serveur). */
  platformAdmin: z.boolean(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const updateMeSchema = z.object({ uiMode: uiModeSchema });

export const changePasswordSchema = z.object({ currentPassword: z.string().min(1).max(200), newPassword: passwordSchema });
export const resetPasswordSchema = z.object({ token: z.string().min(20).max(200), password: passwordSchema });

export const authResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number(),
  user: sessionUserSchema,
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

export const createUserSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  email: emailSchema,
  password: passwordSchema,
  title: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(40).optional(),
  roles: z.array(z.string()).min(1),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  fullName: z.string().trim().min(2).max(120).optional(),
  title: z.string().trim().max(120).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  roles: z.array(z.string()).min(1).optional(),
  status: userStatusSchema.optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
